import React, { useCallback, useMemo, useState } from "react";
import { WorkspaceRoutinesStackScreenProps } from "../WorkspaceRoutinesNavigation";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useOrgListStyleProps } from "../../useOrgHomeDetailContainerStyle";
import { graphql, useLazyLoadQuery } from "react-relay";
import { WorkspaceRoutineTemplateStoreDetailQuery } from "./__generated__/WorkspaceRoutineTemplateStoreDetailQuery.graphql";
import { FlatList } from "react-native";
import {
  AHEmptyScreen,
  AHSeparator,
  AHText,
  ListItem,
  RoutineOccasionStatusCircleRelay,
  Box
} from "arkh";
import { keyExtractorId } from "@/src/common/keyExtractors";
import { SortableColumnTitle } from "../../SortableColumnTitle";
import { HeaderButton } from "@/src/components/mobile/Button";
import { WorkspaceDetailNavigationHeader } from "../../WorkspaceDetailNavigationHeader";
import { FormattedMessage } from "react-intl";
import { Lottie } from "@/src/components/Lottie";
import { WorkspaceRoutinesListItemSchedule } from "../WorkspaceComplianceRoutines/WorkspaceRoutinesListItemSchedule";
import { IssueBadge } from "../WorkspaceComplianceRoutines/IssueBadge";
import { WorkspaceDetailHeader } from "../../WorkspaceDetailHeader";

type Props =
  WorkspaceRoutinesStackScreenProps<"WorkspaceRoutineTemplateStoreDetail">;

function ItemContainer(props: {
  title: React.ReactNode;
  status: React.ReactNode;
  schedule: React.ReactNode;
  issueCount: React.ReactNode;
}) {
  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }}>
      <Box style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
        <Box style={{ width: "70%" }}>{props.title}</Box>
        <Box style={{ width: "30%" }}>{props.schedule}</Box>
      </Box>
      <Box
        style={{ width: 150, flexDirection: "row", alignItems: "flex-start" }}
      >
        {props.issueCount}
      </Box>
      <Box style={{
        width: 60,
        alignItems: "flex-end"
      }}>{props.status}</Box>
    </Box>
  );
}

function InnerWorkspaceRoutineTemplateStoreDetail(
  props: Props & { fetchKey: string }
) {
  const intl_tokens = useIntlTokens();
  const listStyle = useOrgListStyleProps();
  const [order, setOrder] = useState<"ASC" | "DESC">("DESC");
  const [orderBy, setOrderBy] = useState<"name" | "status" | "issueCount">(
    "issueCount"
  );

  const data = useLazyLoadQuery<WorkspaceRoutineTemplateStoreDetailQuery>(
    graphql`
      query WorkspaceRoutineTemplateStoreDetailQuery(
        $routineId: ID!
        $storeId: ID!
      ) {
        routine(id: $routineId) {
          id
          title
        }
        store(id: $storeId) {
          id
          name
          routinesFromTemplate(templateId: $routineId) {
            id
            title
            currentSchedule {
              ...WorkspaceRoutinesListItemSchedule_schedule
            }
            currentOccasion {
              ...RoutineOccasionStatusCircle_RoutineOccasion
              sequenceNumber
              issueCount
            }
          }
        }
      }
    `,
    {
      routineId: props.route.params.routineId,
      storeId: props.route.params.storeId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const listData = useMemo(() => {
    return (
      data?.store?.routinesFromTemplate?.slice().sort((a, b) => {
        if (orderBy === "name") {
          return order === "ASC"
            ? a.title.localeCompare(b.title)
            : b.title.localeCompare(a.title);
        }
        if (orderBy === "issueCount") {
          return order === "ASC"
            ? (a.currentOccasion?.issueCount ?? 0) -
            (b.currentOccasion?.issueCount ?? 0)
            : (b.currentOccasion?.issueCount ?? 0) -
            (a.currentOccasion?.issueCount ?? 0);
        }
        if (orderBy === "status") {
          return 0;
        }
        return 0;
      }) ?? []
    );
  }, [data?.store?.routinesFromTemplate, order, orderBy]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <ListItem
          isLast={index === listData.length - 1}
          onPress={() => {
            if (item.currentOccasion) {
              props.navigation.navigateDeprecated("RoutineOccasion", {
                routineId: item.id,
                sequenceNumber: item.currentOccasion.sequenceNumber,
                storeId: props.route.params.storeId,
              });
            }
          }}
        >
          <ItemContainer
            title={<AHText type="subtitle">{item.title}</AHText>}
            schedule={
              <WorkspaceRoutinesListItemSchedule
                schedule={item.currentSchedule}
              />
            }
            issueCount={
              item.currentOccasion ? (
                <IssueBadge
                  size="smallMedium"
                  count={item.currentOccasion?.issueCount}
                />
              ) : null
            }
            status={
              item.currentOccasion ? (
                <RoutineOccasionStatusCircleRelay
                  occasion={item.currentOccasion}
                />
              ) : null
            }
          />
        </ListItem>
      );
    },
    [listData.length, props.navigation, props.route.params.storeId]
  );

  const ListHeaderComponent = useMemo(() => {
    return (
      <Box spaceTop="medium">
        <ListItem isFirst>
          <ItemContainer
            title={
              <SortableColumnTitle
                order={orderBy === "name" ? order : null}
                onOrderChange={(order) => setOrder(order)}
                onOrderBySelect={() => setOrderBy("name")}
                title={intl_tokens.Name}
              />
            }
            issueCount={
              <SortableColumnTitle
                title={
                  <FormattedMessage
                    defaultMessage="Active Issues"
                    description="WorkspaceRoutinesList issueCount column"
                  />
                }
                onOrderBySelect={() => {
                  setOrderBy("issueCount");
                }}
                order={orderBy === "issueCount" ? order : null}
                onOrderChange={setOrder}
              />
            }
            schedule={
              <SortableColumnTitle
                title={
                  <FormattedMessage
                    defaultMessage="Schedule"
                    description="WorkspaceRoutinesList schedule column"
                  />
                }
                onOrderBySelect={() => {
                  //setOrderBy("freq");
                }}
                order={null}
                onOrderChange={setOrder}
              />
            }
            status={
              <SortableColumnTitle
                order={orderBy === "status" ? order : null}
                onOrderChange={(order) => setOrder(order)}
                onOrderBySelect={() => setOrderBy("status")}
                title={intl_tokens.Status}
              />
            }
          />
        </ListItem>
        <AHSeparator />
      </Box>
    );
  }, [intl_tokens.Name, intl_tokens.Status, order, orderBy]);

  if (listData.length === 0) {
    return (
      <AHEmptyScreen
        illustration={
          <Lottie
            autoPlay
            loop
            source={require("../../../../assets/shake-a-empty-box.json")}
          />
        }
        title={
          <FormattedMessage
            defaultMessage="{routineName} not used in {storeName}"
            description="WorkspaceRoutineTemplateStoreDetail Empty Screen title"
            values={{
              storeName: data?.store?.name,
              routineName: data.routine?.title,
            }}
          />
        }
        subtitle={
          <FormattedMessage
            defaultMessage="{storeName} does not have any checklists created from {routineName}"
            description="WorkspaceRoutineTemplateStoreDetail Empty Screen subtitle"
            values={{
              storeName: data?.store?.name,
              routineName: data.routine?.title,
            }}
          />
        }
      />
    );
  }

  return (
    <>
      <Box
        spaceHorizontal="medium"
        style={{
          maxWidth: 900,
          width: "100%",
          alignSelf: "center",
        }}
        spaceTop="medium"
      >
        <WorkspaceDetailHeader
          color="surface-background-light"
          spaceBottom="none"
          spaceTop="none"
          title={data.store?.name}
        />
        <AHText spaceHorizontal="medium">
          <FormattedMessage
            defaultMessage="Checklists created from {routineName}"
            description="WorkspaceRoutineTemplateStoreDetail Header subtitle"
            values={{
              routineName: data.routine?.title,
            }}
          />
        </AHText>
      </Box>
      <FlatList
        data={listData}
        renderItem={renderItem}
        keyExtractor={keyExtractorId}
        ListHeaderComponent={ListHeaderComponent}
        ItemSeparatorComponent={AHSeparator}
        {...listStyle}
      />
    </>
  );
}

export function WorkspaceRoutineTemplateStoreDetail(props: Props) {
  return (
    <>
      <WorkspaceDetailNavigationHeader
        left={
          <HeaderButton
            type="WEB-BACK"
            onPress={() => {
              props.navigation.goBack();
            }}
          />
        }
        right={null}
      />
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => (
          <InnerWorkspaceRoutineTemplateStoreDetail
            fetchKey={fetchKey}
            {...props}
          />
        )}
      </LazyQueryNetworkBoundrary>
    </>
  );
}
